import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import {
	type MouseEvent as ReactMouseEvent,
	type TouchEvent as ReactTouchEvent,
	useCallback,
	useEffect,
	useRef,
	useState,
	type WheelEvent as ReactWheelEvent
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import type { DcrAttachment } from '../attachment.types';

import { attachmentViewerStyles as sx } from './AttachmentViewer.styles';

const MIN_ZOOM = 1;
const MAX_ZOOM = 15;
const ZOOM_STEP = 0.25;

type Props = {
	attachments: DcrAttachment[];
	initialIndex: number;
	onClose: () => void;
};

export const AttachmentViewer = ({
	attachments,
	initialIndex,
	onClose
}: Props) => {
	const { t } = useTranslation();
	const [index, setIndex] = useState(initialIndex);
	const [zoom, setZoom] = useState(1);
	const [offset, setOffset] = useState({ x: 0, y: 0 });
	const [isDragging, setIsDragging] = useState(false);

	const dragStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });
	const didDragRef = useRef(false);
	const imgRef = useRef<HTMLImageElement | null>(null);
	const stageRef = useRef<HTMLDivElement | null>(null);
	const pinchStartRef = useRef<{ distance: number; zoom: number } | null>(null);
	const zoomRef = useRef(1);

	const clampOffset = useCallback(
		(x: number, y: number, atZoom = zoom) => {
			const img = imgRef.current;
			if (!img) return { x, y };

			const scaledW = img.offsetWidth * atZoom;
			const scaledH = img.offsetHeight * atZoom;

			const maxX = (scaledW + window.innerWidth) / 2;
			const maxY = (scaledH + window.innerHeight) / 2;

			return {
				x: Math.max(-maxX, Math.min(maxX, x)),
				y: Math.max(-maxY, Math.min(maxY, y))
			};
		},
		[zoom]
	);

	const current = attachments[index];

	const resetTransform = useCallback(() => {
		zoomRef.current = 1;
		setZoom(1);
		setOffset({ x: 0, y: 0 });
	}, []);

	const handlePrev = useCallback(() => {
		const len = attachments.length;
		setIndex(i => (i - 1 + len) % len);
		resetTransform();
	}, [attachments.length, resetTransform]);

	const handleNext = useCallback(() => {
		const len = attachments.length;
		setIndex(i => (i + 1) % len);
		resetTransform();
	}, [attachments.length, resetTransform]);

	useEffect(() => {
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
			else if (e.key === 'ArrowLeft') handlePrev();
			else if (e.key === 'ArrowRight') handleNext();
		};

		window.addEventListener('keydown', handleKey);

		return () => window.removeEventListener('keydown', handleKey);
	}, [onClose, handlePrev, handleNext]);

	const applyZoom = (next: number, focal?: { x: number; y: number }) => {
		const prevZoom = zoomRef.current;
		const clamped = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));

		if (clamped === prevZoom) return;

		zoomRef.current = clamped;
		setZoom(clamped);

		if (clamped === MIN_ZOOM) {
			setOffset({ x: 0, y: 0 });
			return;
		}

		const stage = stageRef.current;
		if (!focal || !stage || prevZoom === 0) {
			setOffset(o => clampOffset(o.x, o.y, clamped));
			return;
		}

		const rect = stage.getBoundingClientRect();
		const cursorRelX = focal.x - (rect.left + rect.width / 2);
		const cursorRelY = focal.y - (rect.top + rect.height / 2);
		const ratio = clamped / prevZoom;

		setOffset(o =>
			clampOffset(
				o.x * ratio + cursorRelX * (1 - ratio),
				o.y * ratio + cursorRelY * (1 - ratio),
				clamped
			)
		);
	};

	const handleWheel = (e: ReactWheelEvent) => {
		e.preventDefault();
		const direction = e.deltaY < 0 ? 1 : -1;

		applyZoom(zoomRef.current + direction * ZOOM_STEP, {
			x: e.clientX,
			y: e.clientY
		});
	};

	const touchDistance = (
		a: { clientX: number; clientY: number },
		b: { clientX: number; clientY: number }
	) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

	const handleTouchStart = (e: ReactTouchEvent) => {
		if (e.touches.length === 2) {
			pinchStartRef.current = {
				distance: touchDistance(e.touches[0], e.touches[1]),
				zoom
			};
		}
	};

	const handleTouchMove = (e: ReactTouchEvent) => {
		if (e.touches.length === 2 && pinchStartRef.current) {
			e.preventDefault();

			const t0 = e.touches[0];
			const t1 = e.touches[1];
			const distance = touchDistance(t0, t1);
			const ratio = distance / pinchStartRef.current.distance;

			applyZoom(pinchStartRef.current.zoom * ratio, {
				x: (t0.clientX + t1.clientX) / 2,
				y: (t0.clientY + t1.clientY) / 2
			});
		}
	};

	const handleTouchEnd = (e: ReactTouchEvent) => {
		if (e.touches.length < 2) pinchStartRef.current = null;
	};

	const handleMouseDown = (e: ReactMouseEvent) => {
		didDragRef.current = false;

		if (zoom <= 1) return;
		setIsDragging(true);

		dragStartRef.current = {
			x: e.clientX,
			y: e.clientY,
			offsetX: offset.x,
			offsetY: offset.y
		};
	};

	const handleMouseMove = (e: ReactMouseEvent) => {
		if (!isDragging) return;

		const dx = e.clientX - dragStartRef.current.x;
		const dy = e.clientY - dragStartRef.current.y;

		if (Math.abs(dx) > 2 || Math.abs(dy) > 2) didDragRef.current = true;

		setOffset(
			clampOffset(
				dragStartRef.current.offsetX + dx,
				dragStartRef.current.offsetY + dy
			)
		);
	};

	const handleMouseUp = () => setIsDragging(false);

	const handleStageClick = (e: ReactMouseEvent) => {
		if (didDragRef.current) {
			didDragRef.current = false;
			return;
		}

		if (e.target === e.currentTarget) onClose();
	};

	const handleBackdropClick = (e: ReactMouseEvent) => {
		if (e.target === e.currentTarget) onClose();
	};

	if (!current) return null;

	return createPortal(
		<Box sx={sx.backdrop} onClick={handleBackdropClick}>
			<Box sx={sx.topBar}>
				<Typography sx={sx.filename}>{current.name}</Typography>
				<Typography sx={sx.counter}>
					{index + 1} / {attachments.length}
				</Typography>
				<IconButton
					sx={sx.iconButton}
					onClick={onClose}
					aria-label={t('Close')}
				>
					<CloseIcon />
				</IconButton>
			</Box>

			<Box
				ref={stageRef}
				sx={sx.imageStage(isDragging, zoom)}
				onWheel={handleWheel}
				onMouseDown={handleMouseDown}
				onMouseMove={handleMouseMove}
				onMouseUp={handleMouseUp}
				onMouseLeave={handleMouseUp}
				onClick={handleStageClick}
				onTouchStart={handleTouchStart}
				onTouchMove={handleTouchMove}
				onTouchEnd={handleTouchEnd}
				onTouchCancel={handleTouchEnd}
			>
				<Box
					component='img'
					ref={imgRef}
					src={current.previewUrl}
					alt={current.name}
					sx={sx.image(zoom, offset.x, offset.y)}
					draggable={false}
				/>
			</Box>

			{attachments.length > 1 && (
				<>
					<IconButton
						sx={sx.navButton('left')}
						onClick={handlePrev}
						aria-label={t('Previous')}
					>
						<ChevronLeftIcon fontSize='large' />
					</IconButton>
					<IconButton
						sx={sx.navButton('right')}
						onClick={handleNext}
						aria-label={t('Next')}
					>
						<ChevronRightIcon fontSize='large' />
					</IconButton>
				</>
			)}
		</Box>,
		document.body
	);
};
